/**
 * Pathfinder Sentinel Component
 * 
 * AI-powered analysis for route and movement data including:
 * - Route behavior analysis
 * - Speed and movement patterns
 * - Dwell time analysis
 * - Device comparison
 * - Anomaly detection
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Chip,
  Button,
  Stack,
  Menu,
  MenuItem,
  ListItemText,
  alpha,
} from '@mui/material';
import {
  Psychology as AskIcon,
  Send as SendIcon,
  Lightbulb as PromptIcon,
  Speed as SpeedIcon,
  Place as PlaceIcon,
  Route as RouteIcon,
  Compare as CompareIcon,
  CropFree as AreaSelectIcon,
  CheckCircle as CheckIcon,
  HelpOutline as HelpIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { PathfinderDataPoint, PathfinderRoute, pathfinderApi } from '../../services/pathfinderApi';
import { generateSentinelChatPDF } from '@signals/app-shell/components/pdf';
import { apiService } from '@signals/app-shell/services/api';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useNarrativeStore, askSentinel } from '../../stores/narrativeStore';
import { usePathfinderStore } from '../../stores/pathfinderStore';

// Conversion functions for U.S. standard units
const metersToMiles = (meters: number): number => meters * 0.000621371;
const metersToFeet = (meters: number): number => meters * 3.28084;
// const kmhToMph = (kmh: number): number => kmh * 0.621371;
// GPS speed is typically in m/s (meters per second), convert to mph
const mpsToMph = (mps: number): number => mps * 2.23694; // m/s to mph directly

// Maximum realistic speed threshold (in m/s) - filters out GPS noise/spikes
// 75 mph = 33.5 m/s - anything above this is likely GPS error or unrealistic driving
const MAX_REALISTIC_SPEED_MPS = 33.5;

// Format distance in U.S. units (miles for long distances, feet for short)
const formatDistance = (meters: number): string => {
  const miles = metersToMiles(meters);
  if (miles >= 0.1) {
    return `${miles.toFixed(2)} mi`;
  } else {
    const feet = metersToFeet(meters);
    return `${feet.toFixed(0)} ft`;
  }
};

// Format speed in mph (unused)
// const formatSpeed = (kmh: number): string => {
//   return `${kmhToMph(kmh).toFixed(1)} mph`;
// };

// Calculate bearing between two points (in degrees, 0-360)
const calculateBearing = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;
  
  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);
  
  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  bearing = (bearing + 360) % 360;
  return bearing;
};

// Calculate angle difference between two bearings (0-180 degrees)
const angleDifference = (bearing1: number, bearing2: number): number => {
  const diff = Math.abs(bearing1 - bearing2);
  return Math.min(diff, 360 - diff);
};

// Detect significant turns (90-degree changes) along a route
const detectTurnPoints = (points: PathfinderDataPoint[], minTurnAngle: number = 70): number[] => {
  if (points.length < 3) return [];
  
  const turnIndices: number[] = [];
  const minDistance = 50; // Minimum distance in meters between turn points (to avoid noise)
  
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    
    // Calculate bearings
    const bearing1 = calculateBearing(
      prev.coordinates[0], prev.coordinates[1],
      curr.coordinates[0], curr.coordinates[1]
    );
    const bearing2 = calculateBearing(
      curr.coordinates[0], curr.coordinates[1],
      next.coordinates[0], next.coordinates[1]
    );
    
    // Calculate angle change
    const angleChange = angleDifference(bearing1, bearing2);
    
    // Check if this is a significant turn (approximately 90 degrees)
    if (angleChange >= minTurnAngle && angleChange <= 110) {
      // Check distance from last turn point to avoid clustering
      if (turnIndices.length === 0) {
        turnIndices.push(i);
      } else {
        const lastTurnIndex = turnIndices[turnIndices.length - 1];
        const lastTurnPoint = points[lastTurnIndex];
        
        // Simple distance check (Haversine would be better but this is faster)
        const latDiff = Math.abs(curr.coordinates[0] - lastTurnPoint.coordinates[0]);
        const lonDiff = Math.abs(curr.coordinates[1] - lastTurnPoint.coordinates[1]);
        const approxDistance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff) * 111000; // Rough meters
        
        if (approxDistance >= minDistance) {
          turnIndices.push(i);
        }
      }
    }
  }
  
  return turnIndices;
};

// Get road name from database (preferred) or fallback to Mapbox
const getRoadName = async (lat: number, lon: number): Promise<string | null> => {
  try {
    // Try to get road name from database first using the closest-road endpoint
    // Use apiService.client which has authentication interceptors
    const response = await apiService.client.get(
      `/route-geocoding/closest-road`,
      {
        params: {
          lat,
          lon,
          radius_meters: 50
        }
      }
    );
    
    if (response.data?.road_name) {
      return response.data.road_name;
    }
  } catch (error) {
    // Fall through to Mapbox fallback
    console.debug('Database road lookup failed, using Mapbox fallback');
  }
  
  // Fallback to Mapbox reverse geocoding with address preference
  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || (window as any).MAPBOX_TOKEN;
  
  if (!MAPBOX_TOKEN) {
    return null;
  }

  try {
    // Prefer address type to get specific street names, not neighborhoods
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${MAPBOX_TOKEN}&types=address&limit=1`;
    
    const response = await fetch(url);
    if (!response.ok) {
      // If address fails, try poi as fallback
      const poiUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${MAPBOX_TOKEN}&types=poi&limit=1`;
      const poiResponse = await fetch(poiUrl);
      if (poiResponse.ok) {
        const poiData = await poiResponse.json();
        if (poiData.features && poiData.features.length > 0) {
          return poiData.features[0].text || poiData.features[0].place_name || null;
        }
      }
      return null;
    }
    
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      // For addresses, try to extract just the street name (first part before comma)
      const placeName = feature.place_name || feature.text || null;
      if (placeName) {
        // Extract street name (usually first part before first comma)
        const parts = placeName.split(',');
        if (parts.length > 0) {
          return parts[0].trim();
        }
        return placeName;
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Reverse geocode error:', error);
    return null;
  }
};

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: PathfinderAction[];
}

export type PathfinderActionType = 
  | 'select_student'       // Select a student
  | 'select_device'        // Select a device
  | 'zoom_to_location'     // Zoom to specific location
  | 'highlight_route'      // Highlight a route segment
  | 'show_dwells'          // Show dwell locations
  | 'filter_time'          // Filter by time range
  | 'compare_devices'      // Compare two devices
  | 'clear_highlights'     // Clear all highlights
  | 'open_story_mode'      // Open the Story Mode tool (left panel)
  | 'open_story_panel';    // Open the Story HUD card on the map

export interface PathfinderAction {
  type: PathfinderActionType;
  studentId?: string;
  deviceId?: string;
  location?: [number, number];
  timeRange?: { start: string; end: string };
  label: string;
  icon?: React.ReactNode;
  autoExecute?: boolean;
}

interface PathfinderSentinelProps {
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  dwellLocations: any[];
  actualDevices: Record<string, any[]>;
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>;
  timeRange?: { start: Date; end: Date } | null;
  onStudentSelect?: (studentId: string) => void;
  onDeviceSelect?: (deviceId: string, selected: boolean) => void;
  onZoomToLocation?: (coords: [number, number]) => void;
  // Area drawing props
  isDrawingAnalysisArea?: boolean;
  analysisArea?: GeoJSON.Polygon | null;
  onMapAction?: (action: { type: 'start_area_analysis' | 'cancel_area_analysis' }) => void;
  // Itinerary submissions
  itinerarySubmissions?: Array<{
    id?: number;
    student_number: string;
    route_run_datetime: string;
    route_entries: Array<{
      id?: number;
      latitude: number;
      longitude: number;
      location_name_address: string;
      arrive_datetime: string;
      depart_datetime: string;
      route_type: string;
      route_component: string;
    }>;
  }>;
}

/** Data the Sentinel analysis engine reasons over (same inputs the panel receives). */
export interface SentinelEngineInput {
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  dwellLocations: any[];
  actualDevices: Record<string, any[]>;
  itinerarySubmissions?: NonNullable<PathfinderSentinelProps['itinerarySubmissions']>;
}

/**
 * Sentinel analysis engine, shared by the left-panel chat and the map HUD so
 * questions can be asked (and answered identically) from either place.
 */
export function usePathfinderSentinelEngine({
  dataPoints,
  routes,
  dwellLocations,
  actualDevices,
  itinerarySubmissions = [] as NonNullable<PathfinderSentinelProps['itinerarySubmissions']>,
}: SentinelEngineInput) {
  const { singularLower, plural, pluralLower } = useParticipantLabels();

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalPoints = dataPoints.length;
    const totalRoutes = routes.length;
    const totalDwells = dwellLocations.length;
    const totalStudents = Object.keys(actualDevices).length;
    const totalDevices = Object.values(actualDevices).flat().length;

    // Calculate device type distribution
    const deviceTypeCounts: Record<string, number> = {};
    dataPoints.forEach(p => {
      const type = p.deviceType || 'unknown';
      deviceTypeCounts[type] = (deviceTypeCounts[type] || 0) + 1;
    });

    // Time range - use reduce to avoid stack overflow on large datasets
    let minTime = Infinity, maxTime = -Infinity;
    dataPoints.forEach(p => {
      const ts = new Date(p.timestamp).getTime();
      if (ts < minTime) minTime = ts;
      if (ts > maxTime) maxTime = ts;
    });
    const startTime = dataPoints.length > 0 ? new Date(minTime) : null;
    const endTime = dataPoints.length > 0 ? new Date(maxTime) : null;

    return {
      totalPoints,
      totalRoutes,
      totalDwells,
      totalStudents,
      totalDevices,
      deviceTypeCounts,
      startTime,
      endTime,
    };
  }, [dataPoints, routes, dwellLocations, actualDevices]);

  // Generate AI response based on question and data
  const generateResponse = useCallback(async (userQuestion: string): Promise<{ content: string; actions: PathfinderAction[] }> => {
    const q = userQuestion.toLowerCase();
    const actions: PathfinderAction[] = [];
    let response = '';

    // Check for route comparison questions (same roads, split up, diverge, together, compare routes)
    const isRouteComparisonQuestion = q.includes('same road') || q.includes('split') || 
      q.includes('diverge') || q.includes('together') || q.includes('separate') || 
      q.includes('different road') || q.includes('both') || q.includes('share') ||
      (q.includes('compare') && (q.includes('route') || q.includes('phone') || q.includes('vehicle') || q.includes('watch')));
    
    // Check for speeding/speed analysis questions
    const isSpeedQuestion = q.includes('speed') || q.includes('speeding') || q.includes('fast') || 
      q.includes('mph') || q.includes('rapid') || q.includes('quick');
    
    // Check for pattern analysis questions
    const isPatternQuestion = q.includes('pattern') || q.includes('typical') || q.includes('usual') || 
      q.includes('habit') || q.includes('frequent') || q.includes('regular');
    
    // Check for co-traveler/proximity questions
    const isCoTravelerQuestion = q.includes('co-traveler') || q.includes('cotraveler') || 
      q.includes('co traveler') || q.includes('who was near') || q.includes('who were near') ||
      q.includes('proximity') || q.includes('same location') || q.includes('same place') ||
      q.includes('encounter') || q.includes('met with') || q.includes('crossed path') ||
      q.includes('travel with others') || q.includes('heatmap') || q.includes('meet co');
    
    // Check for device separation questions
    const isDeviceSeparationQuestion = q.includes('separate') || q.includes('split') || 
      q.includes('apart') || q.includes('together') || q.includes('leave') ||
      (q.includes('phone') && q.includes('vehicle')) || 
      (q.includes('watch') && (q.includes('phone') || q.includes('stay'))) ||
      q.includes('all devices') || q.includes('devices traveling');
    
    // Co-traveler analysis - detect entities that were at the same place at similar times
    if (isCoTravelerQuestion) {
      response = `👥 **Co-Traveler Analysis**\n\n`;
      
      if (dataPoints.length === 0) {
        response += `No GPS data loaded. Please select an entity and time range to analyze co-travelers.\n`;
      } else {
        // Group data points by student/entity
        const pointsByStudent: Record<string, PathfinderDataPoint[]> = {};
        dataPoints.forEach(p => {
          const studentId = p.studentId || 'unknown';
          if (!pointsByStudent[studentId]) {
            pointsByStudent[studentId] = [];
          }
          pointsByStudent[studentId].push(p);
        });
        
        const studentIds = Object.keys(pointsByStudent);
        
        if (studentIds.length < 2) {
          response += `Only **${studentIds.length}** entity loaded. Co-traveler analysis requires data from multiple entities.\n\n`;
          response += `To find co-travelers:\n`;
          response += `1. Load data for multiple entities in the same time range\n`;
          response += `2. Or use **Link Analysis** page for cross-entity proximity detection\n`;
        } else {
          // Find proximity events between different entities
          const proximityThreshold = 100; // meters
          const timeThreshold = 5; // minutes
          
          interface ProximityEvent {
            entity1: string;
            entity2: string;
            timestamp: Date;
            location: [number, number];
            distance: number;
            locationName?: string;
          }
          
          const proximityEvents: ProximityEvent[] = [];
          
          // Compare each pair of entities
          for (let i = 0; i < studentIds.length; i++) {
            for (let j = i + 1; j < studentIds.length; j++) {
              const entity1 = studentIds[i];
              const entity2 = studentIds[j];
              const points1 = pointsByStudent[entity1];
              const points2 = pointsByStudent[entity2];
              
              // Check each point from entity1 against points from entity2
              for (const p1 of points1) {
                for (const p2 of points2) {
                  // Check temporal proximity
                  const timeDiff = Math.abs(new Date(p1.timestamp).getTime() - new Date(p2.timestamp).getTime()) / (1000 * 60);
                  if (timeDiff > timeThreshold) continue;
                  
                  // Check spatial proximity (Haversine approximation)
                  const lat1 = p1.coordinates[0], lon1 = p1.coordinates[1];
                  const lat2 = p2.coordinates[0], lon2 = p2.coordinates[1];
                  const latDiff = (lat2 - lat1) * 111000;
                  const lonDiff = (lon2 - lon1) * 111000 * Math.cos(lat1 * Math.PI / 180);
                  const distance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);
                  
                  if (distance <= proximityThreshold) {
                    proximityEvents.push({
                      entity1,
                      entity2,
                      timestamp: new Date(p1.timestamp),
                      location: p1.coordinates,
                      distance
                    });
                  }
                }
              }
            }
          }
          
          if (proximityEvents.length === 0) {
            response += `No co-traveler events detected within ${proximityThreshold}m and ${timeThreshold} minutes.\n\n`;
            response += `**Entities analyzed:** ${studentIds.join(', ')}\n`;
            response += `**Time span:** ${summary.startTime?.toLocaleString() || 'N/A'} to ${summary.endTime?.toLocaleString() || 'N/A'}\n`;
          } else {
            // Group proximity events by entity pairs
            const pairCounts: Record<string, { count: number; events: ProximityEvent[] }> = {};
            proximityEvents.forEach(event => {
              const pairKey = [event.entity1, event.entity2].sort().join(' & ');
              if (!pairCounts[pairKey]) {
                pairCounts[pairKey] = { count: 0, events: [] };
              }
              pairCounts[pairKey].count++;
              pairCounts[pairKey].events.push(event);
            });
            
            // Sort by encounter count
            const sortedPairs = Object.entries(pairCounts)
              .sort((a, b) => b[1].count - a[1].count);
            
            response += `Found **${proximityEvents.length} proximity events** between ${sortedPairs.length} entity pairs:\n\n`;
            
            sortedPairs.slice(0, 5).forEach(([pairName, data], idx) => {
              const firstEvent = data.events[0];
              const lastEvent = data.events[data.events.length - 1];
              
              response += `**${idx + 1}. ${pairName}**\n`;
              response += `  • Encounters: **${data.count}**\n`;
              response += `  • First seen together: ${firstEvent.timestamp.toLocaleString()}\n`;
              response += `  • Last seen together: ${lastEvent.timestamp.toLocaleString()}\n`;
              response += `  • Avg distance: ${Math.round(data.events.reduce((sum, e) => sum + e.distance, 0) / data.events.length)}m\n\n`;
              
              // Add action to zoom to first encounter
              if (idx === 0) {
                actions.push({
                  type: 'zoom_to_location',
                  location: [firstEvent.location[1], firstEvent.location[0]], // [lng, lat]
                  label: 'View First Encounter',
                  icon: <PlaceIcon fontSize="small" />,
                });
              }
            });
            
            if (sortedPairs.length > 5) {
              response += `...and ${sortedPairs.length - 5} more entity pairs with proximity events.\n`;
            }
            
            // Co-travel heatmap - cluster locations where co-travel occurred
            response += `\n**Co-Travel Hotspots:**\n`;
            
            // Cluster proximity events by location (grid-based)
            const gridSize = 0.001; // ~100m grid cells
            const locationClusters: Record<string, { count: number; events: ProximityEvent[]; center: [number, number] }> = {};
            
            proximityEvents.forEach(event => {
              const gridLat = Math.round(event.location[0] / gridSize) * gridSize;
              const gridLon = Math.round(event.location[1] / gridSize) * gridSize;
              const gridKey = `${gridLat.toFixed(4)},${gridLon.toFixed(4)}`;
              
              if (!locationClusters[gridKey]) {
                locationClusters[gridKey] = { count: 0, events: [], center: [gridLat, gridLon] };
              }
              locationClusters[gridKey].count++;
              locationClusters[gridKey].events.push(event);
            });
            
            // Sort clusters by event count
            const sortedClusters = Object.entries(locationClusters)
              .sort((a, b) => b[1].count - a[1].count);
            
            sortedClusters.slice(0, 5).forEach(([_, cluster], idx) => {
              const entities = new Set<string>();
              cluster.events.forEach(e => {
                entities.add(e.entity1);
                entities.add(e.entity2);
              });
              
              // Find matching dwell location name if available
              let locationName = 'Unknown area';
              for (const dwell of dwellLocations) {
                const dwellLat = dwell.latitude || dwell.center_latitude;
                const dwellLon = dwell.longitude || dwell.center_longitude;
                if (dwellLat && dwellLon) {
                  const latDiff = Math.abs(cluster.center[0] - dwellLat);
                  const lonDiff = Math.abs(cluster.center[1] - dwellLon);
                  if (latDiff < 0.002 && lonDiff < 0.002) {
                    locationName = dwell.place_name || dwell.place_address || dwell.address || locationName;
                    break;
                  }
                }
              }
              
              response += `${idx + 1}. **${locationName}** - ${cluster.count} co-travel events\n`;
              response += `   └─ Entities: ${Array.from(entities).join(', ')}\n`;
            });
            
            if (sortedClusters.length > 5) {
              response += `\n...and ${sortedClusters.length - 5} more hotspot locations\n`;
            }
          }
        }
      }
    }
    // Device separation analysis - detect when phone/vehicle/watch split apart
    else if (isDeviceSeparationQuestion) {
      response = `**Device Separation Analysis**\n\n`;
      
      if (dataPoints.length === 0) {
        response += `No GPS data loaded. Please load data to analyze device separation.\n`;
      } else {
        // Group data points by student, then by device type
        const studentDevicePoints: Record<string, Record<string, PathfinderDataPoint[]>> = {};
        
        dataPoints.forEach(p => {
          const studentId = p.studentId || 'unknown';
          const deviceType = p.deviceType || 'unknown';
          
          if (!studentDevicePoints[studentId]) {
            studentDevicePoints[studentId] = {};
          }
          if (!studentDevicePoints[studentId][deviceType]) {
            studentDevicePoints[studentId][deviceType] = [];
          }
          studentDevicePoints[studentId][deviceType].push(p);
        });
        
        // Analyze each student's device separation
        Object.entries(studentDevicePoints).forEach(([studentId, devicePoints]) => {
          const deviceTypes = Object.keys(devicePoints);
          
          if (deviceTypes.length < 2) {
            response += `**${studentId}**: Only ${deviceTypes.length} device type loaded (${deviceTypes.join(', ')})\n`;
            response += `  └─ Need multiple device types to analyze separation\n\n`;
            return;
          }
          
          response += `**${studentId}** (${deviceTypes.join(', ')}):\n`;
          
          // Sort points by timestamp for each device
          deviceTypes.forEach(dt => {
            devicePoints[dt].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
          });
          
          // Find separation events - times when devices are far apart
          const separationThreshold = 200; // meters - devices are "separated" if > 200m apart
          const separationEvents: Array<{
            time: Date;
            device1: string;
            device2: string;
            distance: number;
            device1Location: [number, number];
            device2Location: [number, number];
          }> = [];
          
          const togetherEvents: Array<{
            time: Date;
            devices: string[];
            location: [number, number];
          }> = [];
          
          // Compare devices pairwise at similar timestamps
          const timeWindow = 5 * 60 * 1000; // 5 minutes
          
          for (let i = 0; i < deviceTypes.length; i++) {
            for (let j = i + 1; j < deviceTypes.length; j++) {
              const type1 = deviceTypes[i];
              const type2 = deviceTypes[j];
              const points1 = devicePoints[type1];
              const points2 = devicePoints[type2];
              
              // For each point in device 1, find closest time point in device 2
              for (const p1 of points1) {
                const p1Time = new Date(p1.timestamp).getTime();
                
                // Find closest point in device 2
                let closestP2: PathfinderDataPoint | null = null;
                let closestTimeDiff = Infinity;
                
                for (const p2 of points2) {
                  const timeDiff = Math.abs(new Date(p2.timestamp).getTime() - p1Time);
                  if (timeDiff < closestTimeDiff && timeDiff <= timeWindow) {
                    closestTimeDiff = timeDiff;
                    closestP2 = p2;
                  }
                }
                
                if (closestP2) {
                  // Calculate distance
                  const lat1 = p1.coordinates[0], lon1 = p1.coordinates[1];
                  const lat2 = closestP2.coordinates[0], lon2 = closestP2.coordinates[1];
                  const latDiff = (lat2 - lat1) * 111000;
                  const lonDiff = (lon2 - lon1) * 111000 * Math.cos(lat1 * Math.PI / 180);
                  const distance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);
                  
                  if (distance > separationThreshold) {
                    separationEvents.push({
                      time: new Date(p1.timestamp),
                      device1: type1,
                      device2: type2,
                      distance,
                      device1Location: p1.coordinates,
                      device2Location: closestP2.coordinates
                    });
                  } else if (distance < 50) {
                    // Devices are together (< 50m)
                    togetherEvents.push({
                      time: new Date(p1.timestamp),
                      devices: [type1, type2],
                      location: p1.coordinates
                    });
                  }
                }
              }
            }
          }
          
          // Report findings
          if (separationEvents.length === 0 && togetherEvents.length > 0) {
            response += `  **All devices stayed together** throughout the period\n`;
            response += `  └─ ${togetherEvents.length} synchronized data points\n\n`;
          } else if (separationEvents.length > 0) {
            // Find first and last separation
            separationEvents.sort((a, b) => a.time.getTime() - b.time.getTime());
            
            // Group separations by device pair
            const pairSeparations: Record<string, typeof separationEvents> = {};
            separationEvents.forEach(e => {
              const pairKey = [e.device1, e.device2].sort().join(' & ');
              if (!pairSeparations[pairKey]) {
                pairSeparations[pairKey] = [];
              }
              pairSeparations[pairKey].push(e);
            });
            
            Object.entries(pairSeparations).forEach(([pair, events]) => {
              const firstSep = events[0];
              const lastSep = events[events.length - 1];
              const maxDistance = Math.max(...events.map(e => e.distance));
              
              response += `  **${pair} separated:**\n`;
              response += `    • First separation: ${firstSep.time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}\n`;
              response += `    • Last separation: ${lastSep.time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}\n`;
              response += `    • Max distance apart: **${formatDistance(maxDistance)}**\n`;
              response += `    • Separation events: ${events.length}\n\n`;
              
              // Add action to zoom to first separation
              actions.push({
                type: 'zoom_to_location',
                location: [firstSep.device1Location[1], firstSep.device1Location[0]],
                label: `View ${pair} Separation`,
                icon: <CompareIcon fontSize="small" />,
              });
            });
          } else {
            response += `  Not enough overlapping data to determine separation\n\n`;
          }
        });
      }
    }
    // Route-related questions
    else if (q.includes('route') || q.includes('travel') || q.includes('path') || isRouteComparisonQuestion) {
      if (routes.length === 0) {
        response = `No route data is currently loaded. Please select a ${singularLower} and time range to view their routes.`;
      } else if (isRouteComparisonQuestion && routes.length >= 2) {
        // ROUTE COMPARISON: Compare roads traveled by different devices
        response = `**Route Comparison**\n\n`;
        
        // Group routes by device type
        const routesByDevice: Record<string, typeof routes> = {};
        routes.forEach(r => {
          const type = r.deviceType || 'unknown';
          if (!routesByDevice[type]) routesByDevice[type] = [];
          routesByDevice[type].push(r);
        });
        
        const deviceTypes = Object.keys(routesByDevice);
        
        if (deviceTypes.length < 2) {
          response += `Only **${deviceTypes[0] || 'one device type'}** data is loaded. Need at least 2 device types to compare routes.\n`;
        } else {
          // Geocode routes for each device to get road names
          const roadsByDevice: Record<string, Set<string>> = {};
          const roadSegmentsByDevice: Record<string, Array<{ name: string; distance: number }>> = {};
          
          for (const deviceType of deviceTypes) {
            roadsByDevice[deviceType] = new Set<string>();
            roadSegmentsByDevice[deviceType] = [];
            
            // Get roads for this device's routes
            for (const r of routesByDevice[deviceType].slice(0, 2)) { // Limit to first 2 routes per device
              if (r.points && r.points.length > 0) {
                try {
                  const movingPoints = r.points.filter(p => p.speed != null && p.speed > 2);
                  if (movingPoints.length >= 2) {
                    let simplifiedPoints = movingPoints;
                    if (movingPoints.length > 25) {
                      const step = Math.floor(movingPoints.length / 23);
                      simplifiedPoints = [movingPoints[0]];
                      for (let i = step; i < movingPoints.length - 1 && simplifiedPoints.length < 24; i += step) {
                        simplifiedPoints.push(movingPoints[i]);
                      }
                      simplifiedPoints.push(movingPoints[movingPoints.length - 1]);
                    }
                    
                    const apiCoords: [number, number][] = simplifiedPoints.map(p => p.coordinates);
                    const roadData = await pathfinderApi.getRouteRoads(apiCoords, 'driving');
                    
                    if (roadData.roads && roadData.roads.length > 0) {
                      roadData.roads.forEach(road => {
                        if (road.name && road.name.trim()) {
                          roadsByDevice[deviceType].add(road.name.trim());
                          roadSegmentsByDevice[deviceType].push({ name: road.name.trim(), distance: road.distance || 0 });
                        }
                      });
                    }
                  }
                } catch (error) {
                  console.warn(`[Sentinel] Route geocoding failed for ${deviceType}:`, error);
                }
              }
            }
          }
          
          // Compare roads between devices
          const allDeviceRoads = deviceTypes.map(dt => roadsByDevice[dt]);
          
          // Find common roads (traveled by ALL devices)
          const commonRoads = deviceTypes.length > 0 && allDeviceRoads[0] 
            ? Array.from(allDeviceRoads[0]).filter(road => 
                allDeviceRoads.every(deviceRoads => deviceRoads.has(road))
              )
            : [];
          
          // Find unique roads for each device (roads NOT present in any other device)
          const uniqueRoadsByDevice: Record<string, string[]> = {};
          deviceTypes.forEach(dt => {
            uniqueRoadsByDevice[dt] = Array.from(roadsByDevice[dt]).filter(road => 
              // Road is unique if NO other device has it
              !deviceTypes.some(otherDt => otherDt !== dt && roadsByDevice[otherDt].has(road))
            );
          });
          
          // Calculate overlap percentage
          const totalUniqueRoads = new Set<string>();
          deviceTypes.forEach(dt => roadsByDevice[dt].forEach(road => totalUniqueRoads.add(road)));
          const overlapPercent = totalUniqueRoads.size > 0 
            ? Math.round((commonRoads.length / totalUniqueRoads.size) * 100)
            : 0;
          
          // Generate response
          if (commonRoads.length > 0) {
            response += `**Traveled Together** (${overlapPercent}% route overlap):\n`;
            response += `Both devices traveled on these roads:\n`;
            commonRoads.slice(0, 8).forEach(road => {
              response += `  • ${road}\n`;
            });
            if (commonRoads.length > 8) {
              response += `  • ...and ${commonRoads.length - 8} more shared roads\n`;
            }
            response += `\n`;
          } else {
            response += `**No Common Roads Found**\n`;
            response += `The devices appear to have traveled completely separate routes.\n\n`;
          }
          
          // Show unique roads per device
          let anyUnique = false;
          deviceTypes.forEach(dt => {
            if (uniqueRoadsByDevice[dt].length > 0) {
              anyUnique = true;
              response += `**${dt.charAt(0).toUpperCase() + dt.slice(1)} Only:**\n`;
              uniqueRoadsByDevice[dt].slice(0, 5).forEach(road => {
                response += `  • ${road}\n`;
              });
              if (uniqueRoadsByDevice[dt].length > 5) {
                response += `  • ...and ${uniqueRoadsByDevice[dt].length - 5} more\n`;
              }
              response += `\n`;
            }
          });
          
          if (!anyUnique && commonRoads.length > 0) {
            response += `**Conclusion:** The devices traveled the **same route** - no divergence detected!\n`;
          } else if (anyUnique && commonRoads.length > 0) {
            response += `**Conclusion:** Routes **partially overlapped** - devices traveled together on some roads but took different routes on others.\n`;
          } else {
            response += `↔**Conclusion:** Routes were **completely different** - devices did not share any roads.\n`;
          }
        }
      } else {
        // Calculate comprehensive route statistics
        const totalDistance = routes.reduce((sum, r) => sum + (r.totalDistance || 0), 0);
        const avgDistance = routes.length > 0 ? totalDistance / routes.length : 0;
        
        // Calculate actual travel speed from GPS-reported speeds in data points
        // GPS speed is in m/s - filter to points with valid speed readings (> 0.5 m/s ≈ 1.1 mph to exclude stationary)
        // EXCLUDE watch devices - they don't have reliable GPS speed sensors
        // EXCLUDE unrealistic speeds (> 75 mph) which are GPS noise/spikes
        const movingPoints = dataPoints.filter(p => 
          p.speed != null && p.speed > 0.5 && p.speed <= MAX_REALISTIC_SPEED_MPS && p.deviceType !== 'watch'
        );
        let avgActualSpeedMps = 0;
        let maxSpeedMps = 0;
        if (movingPoints.length > 0) {
          let speedSum = 0;
          movingPoints.forEach(p => {
            const speed = p.speed || 0;
            speedSum += speed;
            if (speed > maxSpeedMps) maxSpeedMps = speed;
          });
          avgActualSpeedMps = speedSum / movingPoints.length;
        }
        const avgActualSpeedMph = mpsToMph(avgActualSpeedMps);
        const maxSpeedMph = mpsToMph(maxSpeedMps);
        
        // Calculate durations (elapsed time, not driving time)
        const routesWithMetrics = routes.map(r => {
          const durationMs = r.endTime.getTime() - r.startTime.getTime();
          const durationMinutes = durationMs / (1000 * 60);
          
            // Calculate actual average speed from route's data points (speed is in m/s)
            // Skip speed calculation for watch devices - they don't have reliable GPS speed
            // Exclude unrealistic speeds (> 75 mph) which are GPS noise/spikes
            const routeMovingPoints = r.deviceType === 'watch' 
              ? [] 
              : r.points.filter(p => p.speed != null && p.speed > 0.5 && p.speed <= MAX_REALISTIC_SPEED_MPS);
            const routeAvgSpeedMps = routeMovingPoints.length > 0
              ? routeMovingPoints.reduce((sum, p) => sum + (p.speed || 0), 0) / routeMovingPoints.length
              : 0;
            const routeAvgSpeedMph = mpsToMph(routeAvgSpeedMps);
          
          return {
            ...r,
            durationMinutes,
            actualSpeedMph: routeAvgSpeedMph, // Actual GPS-reported speed while moving
            startLocation: r.points[0] ? [r.points[0].coordinates[0], r.points[0].coordinates[1]] : null,
            endLocation: r.points[r.points.length - 1] ? [r.points[r.points.length - 1].coordinates[0], r.points[r.points.length - 1].coordinates[1]] : null,
            startHour: r.startTime.getHours(),
            dayOfWeek: r.startTime.toLocaleDateString('en-US', { weekday: 'short' })
          };
        });

        // Calculate the ACTUAL time span (from earliest start to latest end), not sum of overlapping durations
        const earliestStart = Math.min(...routesWithMetrics.map(r => r.startTime.getTime()));
        const latestEnd = Math.max(...routesWithMetrics.map(r => r.endTime.getTime()));
        const actualTimeSpanMinutes = (latestEnd - earliestStart) / (1000 * 60);
        
        // Also track individual durations for per-route stats
        // Group by device type with detailed stats
        const routesByDevice: Record<string, {
          count: number;
          totalDistance: number;
          avgDistance: number;
          avgDuration: number;
          avgSpeed: number;
        }> = {};

        routesWithMetrics.forEach(r => {
          const type = r.deviceType || 'unknown';
          if (!routesByDevice[type]) {
            routesByDevice[type] = { count: 0, totalDistance: 0, avgDistance: 0, avgDuration: 0, avgSpeed: 0 };
          }
          routesByDevice[type].count++;
          routesByDevice[type].totalDistance += r.totalDistance || 0;
        });

        // Calculate averages per device type
        Object.keys(routesByDevice).forEach(type => {
          const deviceRoutes = routesWithMetrics.filter(r => (r.deviceType || 'unknown') === type);
          routesByDevice[type].avgDistance = routesByDevice[type].totalDistance / routesByDevice[type].count;
          routesByDevice[type].avgDuration = deviceRoutes.reduce((sum, r) => sum + r.durationMinutes, 0) / deviceRoutes.length;
          routesByDevice[type].avgSpeed = deviceRoutes.reduce((sum, r) => sum + r.actualSpeedMph, 0) / deviceRoutes.length;
        });

        // Create route segments between dwell locations
        // A "route segment" is the travel between two stops/dwells
        interface RouteSegment {
          startDwell: string;
          endDwell: string;
          startTime: Date;
          endTime: Date;
          distance: number; // meters
          deviceType: string;
          points: PathfinderDataPoint[];
        }
        
        const routeSegments: RouteSegment[] = [];
        
        // Debug logging
        console.log(`[Sentinel] Attempting to segment routes. Dwells: ${dwellLocations.length}, DataPoints: ${dataPoints.length}`);
        
        // Debug: Log first few dwells to verify data structure
        if (dwellLocations.length > 0) {
          console.log(`[Sentinel] Sample dwell:`, {
            lat: dwellLocations[0].latitude,
            lon: dwellLocations[0].longitude,
            place_name: dwellLocations[0].place_name,
            entity_id: dwellLocations[0].entity_id,
          });
        }
        if (dataPoints.length > 0) {
          console.log(`[Sentinel] Sample point:`, {
            coords: dataPoints[0].coordinates,
            deviceType: dataPoints[0].deviceType,
            timestamp: dataPoints[0].timestamp,
          });
        }
        
        // Helper to check if a point is within a dwell location
        // Using 300m radius to account for GPS inaccuracy and dwell area size
        const DWELL_MATCH_RADIUS = 300; // meters - increased from 150m for better matching
        let dwellMatchCount = 0; // Debug counter
        
        const isAtDwell = (point: PathfinderDataPoint): { atDwell: boolean; dwellName: string } => {
          for (const dwell of dwellLocations) {
            const dwellLat = dwell.latitude || dwell.center_latitude || dwell.lat;
            const dwellLon = dwell.longitude || dwell.center_longitude || dwell.lon;
            // Use configurable radius for dwell detection
            const radius = dwell.radius || DWELL_MATCH_RADIUS;
            
            if (dwellLat && dwellLon && point.coordinates && point.coordinates.length >= 2) {
              // Coordinates are [lat, lng] format (NOT GeoJSON [lng, lat])
              const pointLat = point.coordinates[0];
              const pointLon = point.coordinates[1];
              
              // Simple distance calculation (approximate for nearby points)
              const latDiff = (pointLat - dwellLat) * 111000; // ~111km per degree lat
              const lonDiff = (pointLon - dwellLon) * 111000 * Math.cos(dwellLat * Math.PI / 180);
              const distanceM = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);
              
              if (distanceM <= radius) {
                dwellMatchCount++;
                // Use place_name (from COP API) or fallback to other name fields
                return { atDwell: true, dwellName: dwell.place_name || dwell.location_name || dwell.name || 'Unknown Location' };
              }
            }
          }
          return { atDwell: false, dwellName: '' };
        };
        
        // Process each device's points to find segments between dwells
        const deviceTypes = Array.from(
          new Set(dataPoints.map(p => p.deviceType).filter(Boolean))
        ) as string[];
        
        console.log(`[Sentinel] Processing ${deviceTypes.length} device types:`, deviceTypes);
        
        deviceTypes.forEach(deviceType => {
          const devicePoints = dataPoints
            .filter(p => p.deviceType === deviceType)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
          console.log(`[Sentinel] Device ${deviceType}: ${devicePoints.length} points`);
          
          if (devicePoints.length < 2) return;
          
          let currentSegment: { startDwell: string; startTime: Date; points: PathfinderDataPoint[] } | null = null;
          let lastDwellName = '';
          
          for (let i = 0; i < devicePoints.length; i++) {
            const point = devicePoints[i];
            const { atDwell, dwellName } = isAtDwell(point);
            
            if (atDwell) {
              // We're at a dwell location
              if (currentSegment && currentSegment.points.length > 1) {
                // End the current segment - calculate distance
                // Coordinates are [lat, lng] format (NOT GeoJSON [lng, lat])
                let segmentDistance = 0;
                let validPointCount = 0;
                for (let j = 1; j < currentSegment.points.length; j++) {
                  const p1 = currentSegment.points[j - 1];
                  const p2 = currentSegment.points[j];
                  const lat1 = p1.coordinates[0], lon1 = p1.coordinates[1];
                  const lat2 = p2.coordinates[0], lon2 = p2.coordinates[1];
                  
                  // VALIDATE: Skip invalid/null coordinates
                  if (!lat1 || !lon1 || !lat2 || !lon2 ||
                      !isFinite(lat1) || !isFinite(lon1) || !isFinite(lat2) || !isFinite(lon2) ||
                      Math.abs(lat1) > 90 || Math.abs(lat2) > 90 ||
                      Math.abs(lon1) > 180 || Math.abs(lon2) > 180) {
                    continue;
                  }
                  
                  const latDiff = (lat2 - lat1) * 111000;
                  const lonDiff = (lon2 - lon1) * 111000 * Math.cos(lat1 * Math.PI / 180);
                  const pointDistance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);
                  
                  // VALIDATE: Speed-based filter - allows flights but catches GPS teleportation
                  // Max realistic speed: 1000 km/h (620 mph) - faster than any commercial jet
                  const timeDiffMs = new Date(p2.timestamp).getTime() - new Date(p1.timestamp).getTime();
                  const timeDiffHours = timeDiffMs / (1000 * 60 * 60);
                  
                  if (timeDiffHours > 0) {
                    const impliedSpeedKmh = (pointDistance / 1000) / timeDiffHours;
                    if (impliedSpeedKmh > 1000) {
                      console.warn(`[Sentinel] Skipping GPS noise: ${impliedSpeedKmh.toFixed(0)} km/h implied speed`);
                      continue;
                    }
                  } else if (pointDistance > 100) {
                    // Same timestamp but moved > 100m = duplicate/bad data
                    continue;
                  }
                  
                  segmentDistance += pointDistance;
                  validPointCount++;
                }
                
                // Only add segment if distance is meaningful (> 100m) and has valid points
                if (segmentDistance > 100 && validPointCount > 0) {
                  routeSegments.push({
                    startDwell: currentSegment.startDwell,
                    endDwell: dwellName,
                    startTime: currentSegment.startTime,
                    endTime: new Date(point.timestamp),
                    distance: segmentDistance,
                    deviceType: deviceType || 'unknown',
                    points: currentSegment.points
                  });
                }
                currentSegment = null;
              }
              lastDwellName = dwellName;
            } else {
              // We're moving
              if (!currentSegment && lastDwellName) {
                // Start a new segment from the last dwell
                currentSegment = {
                  startDwell: lastDwellName,
                  startTime: new Date(point.timestamp),
                  points: [point]
                };
              } else if (currentSegment) {
                currentSegment.points.push(point);
              }
            }
          }
        });
        
        console.log(`[Sentinel] Dwell matching results: ${dwellMatchCount} points matched to dwells out of ${dataPoints.length} total`);
        console.log(`[Sentinel] Found ${routeSegments.length} route segments between dwells`);
        if (routeSegments.length > 0) {
          console.log(`[Sentinel] Sample segment:`, routeSegments[0]);
        }
        
        // Find longest and shortest SEGMENTS (between dwells)
        const sortedSegments = [...routeSegments].sort((a, b) => b.distance - a.distance);
        const longestSegment = sortedSegments[0];
        const shortestSegment = sortedSegments.length > 0 ? sortedSegments[sortedSegments.length - 1] : null;
        
        // Fall back to full routes if no segments found
        const sortedByDistance = [...routesWithMetrics].sort((a, b) => (b.totalDistance || 0) - (a.totalDistance || 0));
        const longestRoute = sortedByDistance[0];
        const shortestRoute = sortedByDistance[sortedByDistance.length - 1];

        // Time-of-day analysis
        const routesByHour: Record<number, number> = {};
        routesWithMetrics.forEach(r => {
          routesByHour[r.startHour] = (routesByHour[r.startHour] || 0) + 1;
        });
        const peakHour = Object.entries(routesByHour).sort((a, b) => b[1] - a[1])[0];

        // Day-of-week analysis
        const routesByDay: Record<string, number> = {};
        routesWithMetrics.forEach(r => {
          routesByDay[r.dayOfWeek] = (routesByDay[r.dayOfWeek] || 0) + 1;
        });
        const peakDay = Object.entries(routesByDay).sort((a, b) => b[1] - a[1])[0];

        // Build comprehensive response
        response = `**Route Analysis**\n\n`;
        if (routeSegments.length > 0) {
          response += `I analyzed **${routeSegments.length} route segments** between dwell locations across the selected time period.\n\n`;
        } else {
          response += `I analyzed **${routes.length} device tracks** across the selected time period.\n\n`;
        }

        // Overall statistics
        response += `**Overall Statistics:**\n`;
        response += `• Total Distance: **${formatDistance(totalDistance)}**\n`;
        response += `• Average Distance per Route: **${formatDistance(avgDistance)}**\n`;
        response += `• Time Span: **${(actualTimeSpanMinutes / 60).toFixed(1)} hours** (${new Date(earliestStart).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(latestEnd).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})})\n`;
        
        // Show actual GPS-reported speeds if available (watch excluded - no reliable speed sensor)
        if (movingPoints.length > 0) {
          response += `• Average Travel Speed: **${avgActualSpeedMph.toFixed(1)} mph** (phone/vehicle GPS)\n`;
          response += `• Max Speed Recorded: **${maxSpeedMph.toFixed(1)} mph**\n`;
        }

        // Device comparison with detailed metrics
        response += `\n**Device Comparison:**\n`;
        const orderedTypes = ['phone', 'vehicle', 'watch'];
        orderedTypes.forEach(type => {
          if (routesByDevice[type]) {
            const stats = routesByDevice[type];
            response += `• **${type.charAt(0).toUpperCase() + type.slice(1)}**: ${stats.count} routes, `;
            response += `${formatDistance(stats.totalDistance)} total\n`;
          }
        });
        // Show any other device types
        Object.entries(routesByDevice).forEach(([type, stats]) => {
          if (!orderedTypes.includes(type)) {
            response += `• **${type}**: ${stats.count} routes, `;
            response += `${formatDistance(stats.totalDistance)} total\n`;
          }
        });

        // Route Extremes - use segments between dwells if available
        if (routeSegments.length > 0 && longestSegment) {
          response += `\n**Route Extremes** *(segments between stops)*:\n`;
          
          // Format longest segment
          const longestStartTime = longestSegment.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          const longestEndTime = longestSegment.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          response += `• Longest: **${formatDistance(longestSegment.distance)}** `;
          response += `(${longestStartTime}-${longestEndTime}, ${longestSegment.deviceType})\n`;
          response += `  └─ ${longestSegment.startDwell} → ${longestSegment.endDwell}\n`;
          
          // Format shortest segment
          if (shortestSegment && shortestSegment !== longestSegment) {
            const shortestStartTime = shortestSegment.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            const shortestEndTime = shortestSegment.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            response += `• Shortest: **${formatDistance(shortestSegment.distance)}** `;
            response += `(${shortestStartTime}-${shortestEndTime}, ${shortestSegment.deviceType})\n`;
            response += `  └─ ${shortestSegment.startDwell} → ${shortestSegment.endDwell}\n`;
          }
          
          // Show total segments count
          response += `\n*${routeSegments.length} total route segments between ${dwellLocations.length} dwell locations*\n`;
        } else if (longestRoute && shortestRoute) {
          // Fall back to full device routes if no segments found
          response += `\n**Route Extremes** *(full device tracks)*:\n`;
          
          const longestStartTime = longestRoute.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          const longestEndTime = longestRoute.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          response += `• Longest: **${formatDistance(longestRoute.totalDistance)}** `;
          response += `(${longestStartTime}-${longestEndTime}, ${longestRoute.deviceType})\n`;
          
          const shortestStartTime = shortestRoute.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          const shortestEndTime = shortestRoute.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          response += `• Shortest: **${formatDistance(shortestRoute.totalDistance)}** `;
          response += `(${shortestStartTime}-${shortestEndTime}, ${shortestRoute.deviceType})\n`;
          
          response += `\n*Note: No dwell locations found to segment routes*\n`;
        }

        // Time patterns
        if (peakHour) {
          response += `\n**Time Patterns:**\n`;
          response += `• Peak Travel Hour: **${peakHour[0]}:00** (${peakHour[1]} routes)\n`;
          if (peakDay) {
            response += `• Peak Travel Day: **${peakDay[0]}** (${peakDay[1]} routes)\n`;
          }
        }

        // Route details for specific questions with geocoded locations
        if (q.includes('today') || q.includes('student') || q.includes(singularLower) || q.includes('detail')) {
          response += `\n**Recent Routes:**\n`;
          const recentRoutes = [...routesWithMetrics]
            .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())
            .slice(0, 5);
          
          // Geocode routes to get road names traveled (limit to avoid too many API calls)
          for (let idx = 0; idx < recentRoutes.length; idx++) {
            const r = recentRoutes[idx];
            const startTime = r.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            const endTime = r.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            
            // Track road segments with timestamps
            let roadSegments: Array<{ name: string; enterTime?: Date; exitTime?: Date }> = [];
            
            // routesWithMetrics includes all route properties including points and coordinates
            if (r.points && r.points.length > 0) {
              // Only geocode for first 3 routes to limit API calls
              if (idx < 3) {
                try {
                  // FILTER OUT STATIONARY POINTS - only geocode moving segments
                  // Speed threshold: > 2 km/h (about 1.2 mph) indicates movement
                  const movingPoints = r.points.filter(p => p.speed != null && p.speed > 2);
                  
                  if (movingPoints.length < 2) {
                    console.log('[Sentinel] Not enough moving points for geocoding');
                  } else {
                    // Use moving points for geocoding - more accurate road detection
                    let simplifiedPoints: typeof movingPoints;
                    if (movingPoints.length > 25) {
                      // Sample evenly: start, evenly spaced middle points, end
                      const step = Math.floor(movingPoints.length / 23);
                      simplifiedPoints = [movingPoints[0]];
                      for (let i = step; i < movingPoints.length - 1 && simplifiedPoints.length < 24; i += step) {
                        simplifiedPoints.push(movingPoints[i]);
                      }
                      simplifiedPoints.push(movingPoints[movingPoints.length - 1]);
                    } else {
                      simplifiedPoints = movingPoints;
                    }
                    
                    // Extract coordinates from moving points
                    const apiCoords: [number, number][] = simplifiedPoints.map(p => p.coordinates);
                    
                    console.log(`[Sentinel] Sending ${apiCoords.length} MOVING coordinates to route geocoding API (filtered from ${r.points.length} total)`);
                    
                    const roadData = await pathfinderApi.getRouteRoads(apiCoords, 'driving');
                    
                    if (roadData.roads && roadData.roads.length > 0) {
                      console.log('[Sentinel] Route geocoding returned roads:', roadData.roads.map(rd => rd.name).join(', '));
                      
                      // Estimate timestamps for road segments based on position in route
                      // This is an approximation - we divide the route time proportionally by road distance
                      const totalRouteDist = roadData.total_distance || r.totalDistance || 1;
                      let cumulativeDist = 0;
                      const routeStartMs = r.startTime.getTime();
                      const routeDurationMs = r.endTime.getTime() - routeStartMs;
                      
                      const seenNames = new Set<string>();
                      roadData.roads.forEach(road => {
                        if (road.name && road.name.trim() && !seenNames.has(road.name.trim())) {
                          const name = road.name.trim();
                          seenNames.add(name);
                          
                          // Estimate entry/exit times based on proportional distance
                          const enterFraction = cumulativeDist / totalRouteDist;
                          const roadDist = road.distance || 0;
                          cumulativeDist += roadDist;
                          const exitFraction = cumulativeDist / totalRouteDist;
                          
                          const enterTime = new Date(routeStartMs + (enterFraction * routeDurationMs));
                          const exitTime = new Date(routeStartMs + (exitFraction * routeDurationMs));
                          
                          roadSegments.push({ name, enterTime, exitTime });
                        }
                      });
                    } else {
                      console.log('Route geocoding returned no roads');
                    }
                  }
                } catch (error) {
                  console.warn('Route geocoding failed for route:', error);
                }
              }
            }
            
            // Format elapsed time more clearly (hours and minutes for long durations)
            const elapsedHours = Math.floor(r.durationMinutes / 60);
            const elapsedMins = Math.round(r.durationMinutes % 60);
            const elapsedStr = elapsedHours > 0 
              ? `${elapsedHours}h ${elapsedMins}m`
              : `${elapsedMins} min`;
            
            // Show actual travel speed if available
            const speedStr = r.actualSpeedMph > 0 
              ? `, avg ${r.actualSpeedMph.toFixed(0)} mph`
              : '';
            
            response += `${idx + 1}. ${formatDistance(r.totalDistance)} `;
            response += `(${startTime}-${endTime}, ${elapsedStr}${speedStr}) [${r.deviceType}]\n`;
            
            // Show road segments with timestamps
            if (roadSegments.length > 0) {
              // Show up to 8 road segments with their times
              const segmentsToShow = roadSegments.slice(0, 8);
              segmentsToShow.forEach((seg, segIdx) => {
                const enterStr = seg.enterTime 
                  ? seg.enterTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                  : '?';
                const exitStr = seg.exitTime 
                  ? seg.exitTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                  : '?';
                const prefix = segIdx === segmentsToShow.length - 1 ? '└─' : '├─';
                response += `   ${prefix} ${seg.name} (${enterStr}-${exitStr})\n`;
              });
              if (roadSegments.length > 8) {
                response += `   └─ ...and ${roadSegments.length - 8} more roads\n`;
              }
            } else if (idx < 3) {
              // If geocoding failed, show start/end locations as fallback
              if (r.points && r.points.length > 0) {
                const startPoint = r.points[0];
                const endPoint = r.points[r.points.length - 1];
                const [startLat, startLon] = startPoint.coordinates;
                const [endLat, endLon] = endPoint.coordinates;
                
                const startName = await getRoadName(startLat, startLon);
                const endName = await getRoadName(endLat, endLon);
                
                if (startName || endName) {
                  response += `   └─ ${startName || 'Start'} → ${endName || 'End'}\n`;
                }
              }
            }
          }
        }

        // Speed analysis using actual GPS-reported speeds
        if (q.includes('speed') || q.includes('fast') || q.includes('slow')) {
          const fastRoutes = routesWithMetrics.filter(r => r.actualSpeedMph > 37); // >60 km/h = ~37 mph
          const slowRoutes = routesWithMetrics.filter(r => r.actualSpeedMph > 0 && r.actualSpeedMph < 15); // slow travel
          response += `\n**Speed Analysis:**\n`;
          if (fastRoutes.length > 0) {
            response += `• High-speed routes (>37 mph): **${fastRoutes.length}** routes\n`;
          }
          if (slowRoutes.length > 0) {
            response += `• Low-speed routes (<3 mph): **${slowRoutes.length}** routes (likely walking/dwells)\n`;
          }
        }

        // Include itinerary data if available
        if (itinerarySubmissions.length > 0) {
          const totalItineraryEntries = itinerarySubmissions.reduce((sum, sub) => sum + sub.route_entries.length, 0);
          response += `\n**Itinerary Submissions:**\n`;
          response += `• Submissions: **${itinerarySubmissions.length}**\n`;
          response += `• Planned Route Entries: **${totalItineraryEntries}**\n`;
          
          if (q.includes('itinerary') || q.includes('planned') || q.includes('coordination')) {
            itinerarySubmissions.forEach((sub, idx) => {
              response += `\n**Submission ${idx + 1}** (${sub.student_number}):\n`;
              sub.route_entries.slice(0, 5).forEach((entry, eIdx) => {
                const arriveTime = new Date(entry.arrive_datetime).toLocaleString();
                response += `  ${eIdx + 1}. ${entry.location_name_address} (${entry.route_type}) - Arrive: ${arriveTime}\n`;
              });
              if (sub.route_entries.length > 5) {
                response += `  ... and ${sub.route_entries.length - 5} more entries\n`;
              }
            });
          }
        }
      }
    }
    // Speed-specific questions
    else if (isSpeedQuestion) {
      // Get all data points with speed values
      // EXCLUDE watch devices - they don't have reliable GPS speed sensors
      // EXCLUDE unrealistic speeds (> 75 mph) which are GPS noise/spikes
      const pointsWithSpeed = dataPoints.filter(p => 
        p.speed != null && p.speed > 0 && p.speed <= MAX_REALISTIC_SPEED_MPS && p.deviceType !== 'watch'
      );
      const hasWatchData = dataPoints.some(p => p.deviceType === 'watch');
      
      if (pointsWithSpeed.length === 0) {
        response = `**Speed Analysis**\n\nNo speed data available in the current dataset. Speed is recorded from GPS-enabled devices (phone/vehicle) during movement.`;
        if (hasWatchData) {
          response += `\n\n*Note: Watch data is excluded from speed analysis as smartwatches don't have reliable GPS speed sensors.*`;
        }
      } else {
        // Calculate stats without spread operator to avoid stack overflow on large datasets
        // GPS speed is in m/s (meters per second)
        let speedSum = 0, maxSpeedMps = 0, minSpeedMps = Infinity;
        pointsWithSpeed.forEach(p => {
          const speed = p.speed!;
          speedSum += speed;
          if (speed > maxSpeedMps) maxSpeedMps = speed;
          if (speed > 0.5 && speed < minSpeedMps) minSpeedMps = speed; // > 0.5 m/s ≈ 1 mph
        });
        const avgSpeedMps = speedSum / pointsWithSpeed.length;
        if (minSpeedMps === Infinity) minSpeedMps = 0;
        
        const avgSpeedMph = mpsToMph(avgSpeedMps);
        const maxSpeedMph = mpsToMph(maxSpeedMps);
        const minSpeedMph = mpsToMph(minSpeedMps);
        
        response = `🚗 **Speed Analysis**\n\n`;
        response += `**Overall Statistics:**\n`;
        response += `• Average Speed: **${avgSpeedMph.toFixed(1)} mph**\n`;
        response += `• Maximum Speed: **${maxSpeedMph.toFixed(1)} mph**\n`;
        response += `• Minimum Moving Speed: **${minSpeedMph.toFixed(1)} mph**\n`;
        response += `• Data Points with Speed: **${pointsWithSpeed.length.toLocaleString()}**\n\n`;
        
        // Speeding incidents (over 65 mph for highway, 35 mph for residential)
        if (q.includes('speeding') || q.includes('incident') || q.includes('violation')) {
          const highwaySpeedMps = 29; // m/s (~65 mph)
          const residentialSpeedMps = 15.6; // m/s (~35 mph)
          
          const highSpeedIncidents = pointsWithSpeed.filter(p => p.speed! > highwaySpeedMps);
          const moderateSpeedIncidents = pointsWithSpeed.filter(p => p.speed! > residentialSpeedMps && p.speed! <= highwaySpeedMps);
          
          response += `**Speeding Analysis:**\n`;
          if (highSpeedIncidents.length > 0) {
            response += `• **High Speed (>65 mph):** ${highSpeedIncidents.length} instances\n`;
            // Show top 3 fastest
            const sortedHigh = [...highSpeedIncidents].sort((a, b) => (b.speed || 0) - (a.speed || 0));
            sortedHigh.slice(0, 3).forEach((p, i) => {
              const time = new Date(p.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
              response += `  ${i + 1}. **${mpsToMph(p.speed!).toFixed(1)} mph** at ${time}\n`;
            });
          } else {
            response += `• No instances exceeding 65 mph\n`;
          }
          
          if (moderateSpeedIncidents.length > 0) {
            response += `• **Moderate Speed (35-65 mph):** ${moderateSpeedIncidents.length} instances\n`;
          }
        }
        
        // Rapid movement changes
        else if (q.includes('rapid') || q.includes('change') || q.includes('acceleration')) {
          response += `**Rapid Movement Changes:**\n`;
          
          // Group points by time and find sudden speed changes
          const sortedPoints = [...pointsWithSpeed].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          
          const rapidChanges: Array<{ time: Date; speedChange: number; from: number; to: number }> = [];
          for (let i = 1; i < sortedPoints.length; i++) {
            const speedDiff = Math.abs((sortedPoints[i].speed || 0) - (sortedPoints[i-1].speed || 0));
            const timeDiff = (new Date(sortedPoints[i].timestamp).getTime() - 
                            new Date(sortedPoints[i-1].timestamp).getTime()) / 1000; // seconds
            
            // Rapid change = >5.4 m/s change in <30 seconds (~12 mph)
            if (speedDiff > 5.4 && timeDiff < 30 && timeDiff > 0) {
              rapidChanges.push({
                time: new Date(sortedPoints[i].timestamp),
                speedChange: speedDiff,
                from: sortedPoints[i-1].speed || 0,
                to: sortedPoints[i].speed || 0,
              });
            }
          }
          
          if (rapidChanges.length > 0) {
            response += `Found **${rapidChanges.length}** rapid speed changes (>12 mph in <30s):\n`;
            rapidChanges.slice(0, 5).forEach((change, i) => {
              const time = change.time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
              const fromMph = mpsToMph(change.from).toFixed(0);
              const toMph = mpsToMph(change.to).toFixed(0);
              const direction = change.to > change.from ? '↑' : '↓';
              response += `${i + 1}. ${time}: ${fromMph} → ${toMph} mph ${direction}\n`;
            });
          } else {
            response += `No significant rapid speed changes detected.\n`;
          }
        }
        
        // Fastest segments
        else if (q.includes('fastest') || q.includes('quickest') || q.includes('top speed')) {
          response += `**🏎️ Fastest Travel Segments:**\n`;
          
          // Find the fastest points
          const sortedBySpeed = [...pointsWithSpeed].sort((a, b) => (b.speed || 0) - (a.speed || 0));
          const topSpeeds = sortedBySpeed.slice(0, 10);
          
          topSpeeds.forEach((p, i) => {
            const time = new Date(p.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            const date = new Date(p.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const device = p.deviceType || 'unknown';
            response += `${i + 1}. **${mpsToMph(p.speed!).toFixed(1)} mph** - ${date} ${time} [${device}]\n`;
          });
          
          // Add action to zoom to fastest point
          if (topSpeeds[0]) {
            actions.push({
              type: 'zoom_to_location',
              location: [topSpeeds[0].coordinates[1], topSpeeds[0].coordinates[0]],
              label: 'View Fastest Point',
              icon: <SpeedIcon fontSize="small" />,
            });
          }
        }
        
        // General speed analysis (average speed during travel)
        else {
          // Speed by device type (speeds are in m/s) - watch already excluded from pointsWithSpeed
          const deviceTypeStrings = pointsWithSpeed.map(p => p.deviceType).filter((type): type is 'phone' | 'vehicle' => type !== undefined && type !== 'watch');
          const deviceTypes: string[] = Array.from(new Set(deviceTypeStrings));
          
          if (deviceTypes.length > 1) {
            response += `**Speed by Device:**\n`;
            deviceTypes.forEach(dt => {
              if (dt) {
                const devicePoints = pointsWithSpeed.filter(p => p.deviceType === dt);
                let speedSum = 0, maxSpeed = 0;
                devicePoints.forEach(p => {
                  const speed = p.speed || 0;
                  speedSum += speed;
                  if (speed > maxSpeed) maxSpeed = speed;
                });
                const avgSpeed = devicePoints.length > 0 ? speedSum / devicePoints.length : 0;
                response += `• ${dt.charAt(0).toUpperCase() + dt.slice(1)}: Avg ${mpsToMph(avgSpeed).toFixed(1)} mph, Max ${mpsToMph(maxSpeed).toFixed(1)} mph\n`;
              }
            });
          }
          
          // Add note about watch exclusion if watch data exists
          if (hasWatchData) {
            response += `\n*Note: Watch data excluded - smartwatches don't have reliable GPS speed sensors.*\n`;
          }
          
          // Time-based speed patterns
          response += `\n**Speed Patterns by Time of Day:**\n`;
          const hourlySpeed: Record<number, number[]> = {};
          pointsWithSpeed.forEach(p => {
            const hour = new Date(p.timestamp).getHours();
            if (!hourlySpeed[hour]) hourlySpeed[hour] = [];
            hourlySpeed[hour].push(p.speed!);
          });
          
          // Find peak hours
          const hourlyAvg = Object.entries(hourlySpeed).map(([hour, speeds]) => ({
            hour: parseInt(hour),
            avg: speeds.reduce((a, b) => a + b, 0) / speeds.length,
          })).sort((a, b) => b.avg - a.avg);
          
          if (hourlyAvg.length > 0) {
            response += `• Fastest Hour: **${hourlyAvg[0].hour}:00** (avg ${mpsToMph(hourlyAvg[0].avg).toFixed(1)} mph)\n`;
            if (hourlyAvg.length > 1) {
              const slowest = hourlyAvg[hourlyAvg.length - 1];
              response += `• Slowest Hour: **${slowest.hour}:00** (avg ${mpsToMph(slowest.avg).toFixed(1)} mph)\n`;
            }
          }
        }
      }
    }
    // Movement/distance-related questions (non-speed focused)
    else if (q.includes('distance') || (q.includes('travel') && !isRouteComparisonQuestion)) {
      const totalDistance = routes.reduce((sum, r) => sum + (r.totalDistance || 0), 0);
      const avgDistance = routes.length > 0 ? totalDistance / routes.length : 0;
      
      response = `**Distance Analysis**\n\n`;
      response += `• Total Distance: **${formatDistance(totalDistance)}**\n`;
      response += `• Route Segments: **${routes.length}**\n`;
      response += `• Avg Segment Length: **${formatDistance(avgDistance)}**\n`;
      
      const longRoutes = routes.filter(r => (r.totalDistance || 0) > 16093); // > 10km = ~6.2 miles = 16093 meters
      if (longRoutes.length > 0) {
        response += `\nFound **${longRoutes.length} long-distance routes** (>6.2 mi)\n`;
      }
    }
    // Travel pattern analysis
    else if (isPatternQuestion) {
      response = `**Travel Pattern Analysis**\n\n`;
      
      if (routes.length === 0 && dataPoints.length === 0) {
        response += `No travel data loaded to analyze patterns.\n`;
      } else {
        // Day of week patterns
        const dayCount: Record<string, number> = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        dataPoints.forEach(p => {
          const day = dayNames[new Date(p.timestamp).getDay()];
          dayCount[day]++;
        });
        
        const sortedDays = Object.entries(dayCount).sort((a, b) => b[1] - a[1]);
        const mostActiveDay = sortedDays.find(d => d[1] > 0);
        
        response += `**Weekly Pattern:**\n`;
        if (mostActiveDay) {
          response += `• Most Active Day: **${mostActiveDay[0]}** (${mostActiveDay[1].toLocaleString()} data points)\n`;
        }
        
        // Hour of day patterns
        const hourCount: Record<number, number> = {};
        dataPoints.forEach(p => {
          const hour = new Date(p.timestamp).getHours();
          hourCount[hour] = (hourCount[hour] || 0) + 1;
        });
        
        const sortedHours = Object.entries(hourCount).sort((a, b) => b[1] - a[1]);
        const peakHour = sortedHours[0];
        
        response += `\n**Daily Pattern:**\n`;
        if (peakHour) {
          const hourNum = parseInt(peakHour[0]);
          const hourStr = hourNum === 0 ? '12 AM' : hourNum < 12 ? `${hourNum} AM` : hourNum === 12 ? '12 PM' : `${hourNum - 12} PM`;
          response += `• Peak Activity Hour: **${hourStr}** (${peakHour[1].toLocaleString()} data points)\n`;
        }
        
        // Morning vs afternoon vs evening
        let morning = 0, afternoon = 0, evening = 0, night = 0;
        dataPoints.forEach(p => {
          const hour = new Date(p.timestamp).getHours();
          if (hour >= 5 && hour < 12) morning++;
          else if (hour >= 12 && hour < 17) afternoon++;
          else if (hour >= 17 && hour < 21) evening++;
          else night++;
        });
        
        const periods = [
          { name: 'Morning (5AM-12PM)', count: morning },
          { name: 'Afternoon (12PM-5PM)', count: afternoon },
          { name: 'Evening (5PM-9PM)', count: evening },
          { name: 'Night (9PM-5AM)', count: night },
        ].sort((a, b) => b.count - a.count);
        
        response += `\n**Time of Day Distribution:**\n`;
        periods.forEach(p => {
          const pct = dataPoints.length > 0 ? Math.round((p.count / dataPoints.length) * 100) : 0;
          response += `• ${p.name}: **${pct}%**\n`;
        });
        
        // Typical dwell locations
        if (dwellLocations.length > 0) {
          response += `\n**Frequent Stop Locations:**\n`;
          const sortedDwells = [...dwellLocations].sort((a, b) => 
            (b.total_visits || b.duration_seconds || 0) - (a.total_visits || a.duration_seconds || 0)
          );
          sortedDwells.slice(0, 5).forEach((d, i) => {
            const name = d.place_name || d.place_address || d.address || `Location ${i + 1}`;
            const visits = d.total_visits || 1;
            response += `${i + 1}. ${name} (${visits} visits)\n`;
          });
        }
        
        // Route characteristics
        if (routes.length > 0) {
          const avgDistance = routes.reduce((sum, r) => sum + (r.totalDistance || 0), 0) / routes.length;
          const avgDuration = routes.reduce((sum, r) => {
            if (r.points && r.points.length >= 2) {
              const startTime = new Date(r.points[0].timestamp).getTime();
              const endTime = new Date(r.points[r.points.length - 1].timestamp).getTime();
              return sum + (endTime - startTime);
            }
            return sum;
          }, 0) / routes.length;
          
          response += `\n**Typical Route Characteristics:**\n`;
          response += `• Average Route Distance: **${formatDistance(avgDistance)}**\n`;
          if (avgDuration > 0) {
            const durationMin = Math.round(avgDuration / 60000);
            response += `• Average Route Duration: **${durationMin} min**\n`;
          }
        }
      }
    }
    // Dwell-related questions
    else if (q.includes('dwell') || q.includes('stop') || q.includes('stay') || q.includes('time spent') || q.includes('unexpected')) {
      if (dwellLocations.length === 0) {
        response = "No dwell locations found in the current data. Dwells are detected when a device stays in one location for an extended period.";
      } else {
        // Helper to format duration from seconds
        const formatDuration = (dwell: any): string => {
          // Try duration_seconds first (preferred)
          let totalSeconds = 0;
          if (dwell.duration_seconds && typeof dwell.duration_seconds === 'number') {
            totalSeconds = dwell.duration_seconds;
          } else if (dwell.duration && typeof dwell.duration === 'string') {
            // Parse timedelta string like "0:54:34.565000"
            const parts = dwell.duration.split(':');
            if (parts.length >= 3) {
              const hours = parseInt(parts[0]) || 0;
              const minutes = parseInt(parts[1]) || 0;
              const seconds = parseFloat(parts[2]) || 0;
              totalSeconds = hours * 3600 + minutes * 60 + seconds;
            }
          }
          
          if (totalSeconds === 0) return 'unknown';
          
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.round((totalSeconds % 3600) / 60);
          
          if (hours > 0) {
            return `${hours}h ${minutes}m`;
          } else {
            return `${minutes} min`;
          }
        };
        
        // Helper to get location name
        const getLocationName = (dwell: any): string => {
          if (dwell.place_name && dwell.place_name.trim()) return dwell.place_name;
          if (dwell.place_address && dwell.place_address.trim()) return dwell.place_address;
          if (dwell.address && dwell.address.trim()) return dwell.address;
          if (dwell.latitude && dwell.longitude) {
            return `${dwell.latitude.toFixed(4)}, ${dwell.longitude.toFixed(4)}`;
          }
          return 'Unknown Location';
        };
        
        // Sort dwells by duration (longest first)
        const sortedDwells = [...dwellLocations].sort((a, b) => {
          const durA = a.duration_seconds || 0;
          const durB = b.duration_seconds || 0;
          return durB - durA;
        });
        
        // Check if asking about "unexpected" stops
        const isUnexpectedQuery = q.includes('unexpected') || q.includes('unusual') || q.includes('strange');
        
        if (isUnexpectedQuery) {
          response = `**Unexpected Stop Analysis**\n\n`;
          
          // Define "unexpected" criteria:
          // 1. Short stops (< 5 min) in random places
          // 2. Stops without a recognized place name
          // 3. Stops at unusual times (if we had time data)
          
          const shortStops = sortedDwells.filter(d => {
            const seconds = d.duration_seconds || 0;
            return seconds > 60 && seconds < 300; // 1-5 minutes
          });
          
          const unknownLocations = sortedDwells.filter(d => 
            !d.place_name && !d.place_address && !d.address
          );
          
          const longUnusualStops = sortedDwells.filter(d => {
            const seconds = d.duration_seconds || 0;
            const hasName = d.place_name || d.place_address || d.address;
            // Long stop at unknown location is potentially unexpected
            return seconds > 1800 && !hasName; // > 30 min at unknown place
          });
          
          if (shortStops.length > 0) {
            response += `**Brief Stops** (1-5 min):\n`;
            shortStops.slice(0, 5).forEach((d, i) => {
              const name = getLocationName(d);
              const dur = formatDuration(d);
              const time = d.start_time ? new Date(d.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
              response += `${i + 1}. ${name} - ${dur}${time ? ` (at ${time})` : ''}\n`;
            });
            response += `\n`;
          }
          
          if (longUnusualStops.length > 0) {
            response += `**Long Stops at Unknown Locations** (>30 min):\n`;
            longUnusualStops.slice(0, 5).forEach((d, i) => {
              const name = getLocationName(d);
              const dur = formatDuration(d);
              const time = d.start_time ? new Date(d.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
              response += `${i + 1}. ${name} - ${dur}${time ? ` (at ${time})` : ''}\n`;
            });
            response += `\n`;
          }
          
          if (unknownLocations.length > 0) {
            response += `**Stops at Unidentified Locations:** ${unknownLocations.length} total\n`;
          }
          
          if (shortStops.length === 0 && longUnusualStops.length === 0) {
            response += `No particularly unexpected stops detected. All stops appear to be at recognized locations with reasonable durations.\n`;
          }
        } else {
          response = `**Dwell Analysis**\n\n`;
          response += `Found **${dwellLocations.length} dwell locations** where the ${singularLower} spent significant time.\n\n`;

          // Show top dwells by duration
          const topDwells = sortedDwells.slice(0, 7);
          response += `**Top Locations by Time Spent:**\n`;
          topDwells.forEach((d, i) => {
            const name = getLocationName(d);
            const dur = formatDuration(d);
            const time = d.start_time ? new Date(d.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
            response += `${i + 1}. ${name} - **${dur}**${time ? ` (started ${time})` : ''}\n`;
          });
          
          if (dwellLocations.length > 7) {
            response += `\n...and ${dwellLocations.length - 7} more stops\n`;
          }
        }

        if (dwellLocations[0]) {
          actions.push({
            type: 'zoom_to_location',
            location: [dwellLocations[0].longitude, dwellLocations[0].latitude],
            label: 'View Top Dwell',
            icon: <PlaceIcon fontSize="small" />,
          });
        }
      }
    }
    // Device comparison
    else if (q.includes('device') || q.includes('compare') || q.includes('phone') || q.includes('vehicle') || q.includes('watch')) {
      const deviceTypeStrings = dataPoints.map(p => p.deviceType).filter((type): type is NonNullable<typeof type> => type !== undefined && type !== null);
      const deviceTypes = new Set<string>(deviceTypeStrings);
      
      response = `**Device Analysis**\n\n`;
      response += `**Active Devices:** ${summary.totalDevices} across ${summary.totalStudents} ${pluralLower}\n\n`;
      
      response += `**Data Points by Device Type:**\n`;
      // Always show phone, vehicle, and watch if they exist, in that order
      const orderedTypes: string[] = ['phone', 'vehicle', 'watch'];
      orderedTypes.forEach(type => {
        if (deviceTypes.has(type)) {
          const count = dataPoints.filter(p => p.deviceType === type).length;
          response += `• ${type.charAt(0).toUpperCase() + type.slice(1)}: ${count.toLocaleString()} points\n`;
        }
      });
      // Show any other device types
      deviceTypes.forEach(type => {
        if (!orderedTypes.includes(type)) {
          const count = dataPoints.filter(p => p.deviceType === type).length;
          response += `• ${type}: ${count.toLocaleString()} points\n`;
        }
      });

      if (q.includes('gap') || q.includes('sync')) {
        response += `\n**Coverage Analysis:**\n`;
        response += `The data spans from ${summary.startTime?.toLocaleString() || 'N/A'} to ${summary.endTime?.toLocaleString() || 'N/A'}\n`;
      }
    }
    // General overview
    else if (q.includes('summary') || q.includes('overview') || q.includes('what') || q.includes('show')) {
      response = `**Data Summary**\n\n`;
      response += `• **${plural}:** ${summary.totalStudents}\n`;
      response += `• **Devices:** ${summary.totalDevices}\n`;
      response += `• **GPS Points:** ${summary.totalPoints.toLocaleString()}\n`;
      response += `• **Route Segments:** ${summary.totalRoutes}\n`;
      response += `• **Dwell Locations:** ${summary.totalDwells}\n\n`;

      if (summary.startTime && summary.endTime) {
        response += `**Time Range:**\n`;
        response += `${summary.startTime.toLocaleString()} → ${summary.endTime.toLocaleString()}\n`;
      }

      const totalDistance = routes.reduce((sum, r) => sum + (r.totalDistance || 0), 0);
      response += `\n**Distance:**\n`;
      response += `• Total: ${formatDistance(totalDistance)}\n`;
    }
    // Anomaly detection and route deviations
    else if (q.includes('anomal') || q.includes('unusual') || q.includes('strange') || q.includes('deviation')) {
      response = `**Anomaly & Deviation Analysis**\n\n`;
      
      let anomalyCount = 0;
      
      // 1. Route distance anomalies
      const longRoutes = routes.filter(r => (r.totalDistance || 0) > 80467); // > 50km = ~31 miles
      // const shortRoutes = routes.filter(r => {
      //   const dist = r.totalDistance || 0;
      //   return dist > 0 && dist < 500; // Very short routes (<0.3 mi) might be GPS drift
      // });
      
      if (longRoutes.length > 0) {
        response += `**Long Distance Segments:** ${longRoutes.length} routes exceeding 31 mi\n`;
        longRoutes.slice(0, 2).forEach((r, i) => {
          const dist = formatDistance(r.totalDistance || 0);
          const device = r.deviceType || 'unknown';
          response += `   ${i + 1}. ${dist} [${device}]\n`;
        });
        response += `\n`;
        anomalyCount++;
      }
      
      // 2. Dwell time anomalies
      const longDwells = dwellLocations.filter(d => (d.duration_seconds || 0) / 60 > 60); // >60 min
      const shortDwells = dwellLocations.filter(d => {
        const mins = (d.duration_seconds || 0) / 60;
        return mins > 0 && mins < 2; // 0-2 min
      });
      
      if (longDwells.length > 0) {
        response += `**Extended Stops:** ${longDwells.length} locations with >60 min dwell\n`;
        anomalyCount++;
      }
      if (shortDwells.length > 0) {
        response += `**Brief Stops:** ${shortDwells.length} very short dwells (<2 min)\n`;
        anomalyCount++;
      }
      
      // 3. Speed anomalies (GPS speed is in m/s)
      // Exclude watch devices - they don't have reliable GPS speed sensors
      const pointsWithSpeed = dataPoints.filter(p => 
        p.speed != null && p.speed > 0 && p.deviceType !== 'watch'
      );
      // GPS noise/spike anomalies: speeds > 75 mph are unrealistic and indicate GPS errors
      const gpsNoisePoints = pointsWithSpeed.filter(p => (p.speed || 0) > MAX_REALISTIC_SPEED_MPS);
      // High but realistic speeds: 65-75 mph (highway driving)
      const highSpeedPoints = pointsWithSpeed.filter(p => {
        const speed = p.speed || 0;
        return speed > 29 && speed <= MAX_REALISTIC_SPEED_MPS; // 65-75 mph
      });
      
      if (gpsNoisePoints.length > 0) {
        let maxNoiseSpeed = 0;
        gpsNoisePoints.forEach(p => {
          const speed = p.speed || 0;
          if (speed > maxNoiseSpeed) maxNoiseSpeed = speed;
        });
        response += `**GPS Noise Detected:** ${gpsNoisePoints.length} readings exceeding 75 mph (filtered from analysis)\n`;
        response += `   └─ Peak anomalous reading: ${mpsToMph(maxNoiseSpeed).toFixed(1)} mph\n\n`;
        anomalyCount++;
      }
      
      if (highSpeedPoints.length > 0) {
        response += `**High-Speed Events:** ${highSpeedPoints.length} points at 65-75 mph\n`;
        let maxSpeed = 0;
        highSpeedPoints.forEach(p => {
          const speed = p.speed || 0;
          if (speed > maxSpeed) maxSpeed = speed;
        });
        response += `   └─ Peak: ${mpsToMph(maxSpeed).toFixed(1)} mph\n\n`;
        anomalyCount++;
      }
      
      // 4. Route deviation analysis (compare device routes)
      if (q.includes('deviation') || q.includes('route')) {
        response += `**Route Deviation Analysis:**\n`;
        
        // Check if devices traveled different distances
        const routesByDevice: Record<string, number> = {};
        routes.forEach(r => {
          const type = r.deviceType || 'unknown';
          routesByDevice[type] = (routesByDevice[type] || 0) + (r.totalDistance || 0);
        });
        
        const deviceDistances = Object.entries(routesByDevice);
        if (deviceDistances.length >= 2) {
          const dists = deviceDistances.map(d => d[1]);
          const avgDist = dists.reduce((a, b) => a + b, 0) / dists.length;
          
          const deviations = deviceDistances.filter(([_, dist]) => {
            const deviation = Math.abs(dist - avgDist) / avgDist;
            return deviation > 0.3; // >30% deviation
          });
          
          if (deviations.length > 0) {
            response += `• Significant distance differences between devices:\n`;
            deviceDistances.forEach(([device, dist]) => {
              const deviation = ((dist - avgDist) / avgDist * 100).toFixed(0);
              const sign = parseInt(deviation) > 0 ? '+' : '';
              response += `  └─ ${device}: ${formatDistance(dist)} (${sign}${deviation}% from avg)\n`;
            });
            response += `\n`;
            anomalyCount++;
          } else {
            response += `• All devices traveled similar distances ✅\n\n`;
          }
        }
        
        // Check for frequent direction changes (potential circling/searching behavior)
        routes.forEach((r, idx) => {
          if (r.points && r.points.length > 10) {
            const turnPoints = detectTurnPoints(r.points, 70);
            const turnsPerMile = r.totalDistance ? (turnPoints.length / metersToMiles(r.totalDistance)) : 0;
            
            if (turnsPerMile > 5) { // >5 significant turns per mile is unusual
              response += `• Route ${idx + 1} has high turn frequency (${turnPoints.length} turns, ${turnsPerMile.toFixed(1)}/mi)\n`;
              response += `  └─ Possible circling or searching behavior\n`;
              anomalyCount++;
            }
          }
        });
      }
      
      // 5. Time gap anomalies
      const sortedPoints = [...dataPoints].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      const largeGaps: Array<{ start: Date; end: Date; duration: number }> = [];
      for (let i = 1; i < sortedPoints.length; i++) {
        const gap = new Date(sortedPoints[i].timestamp).getTime() - 
                   new Date(sortedPoints[i-1].timestamp).getTime();
        const gapHours = gap / (1000 * 60 * 60);
        if (gapHours > 2) { // >2 hour gap
          largeGaps.push({
            start: new Date(sortedPoints[i-1].timestamp),
            end: new Date(sortedPoints[i].timestamp),
            duration: gapHours,
          });
        }
      }
      
      if (largeGaps.length > 0) {
        response += `\n🟡 **Data Gaps:** ${largeGaps.length} periods with >2 hour gaps\n`;
        largeGaps.slice(0, 3).forEach((gap, i) => {
          response += `   ${i + 1}. ${gap.start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} → ${gap.end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} (${gap.duration.toFixed(1)} hrs)\n`;
        });
        anomalyCount++;
      }
      
      // Summary
      if (anomalyCount === 0) {
        response += `\n✅ **No significant anomalies detected** in the current data.\n`;
        response += `The travel patterns appear normal with no unusual deviations.\n`;
      } else {
        response += `\n📊 **Summary:** Found **${anomalyCount}** potential anomalies to review.\n`;
      }
    }
    // Default response
    else {
      response = `I can help you analyze route and movement data. Try asking about:\n\n`;
      response += `• **Routes:** "What routes did this ${singularLower} take?"\n`;
      response += `• **Speed:** "What was the average speed?"\n`;
      response += `• **Dwells:** "Where did they spend the most time?"\n`;
      response += `• **Devices:** "Compare phone vs vehicle data"\n`;
      response += `• **Anomalies:** "Are there any unusual patterns?"\n`;
    }

    return { content: response, actions };
  }, [dataPoints, routes, dwellLocations, summary, itinerarySubmissions, singularLower, pluralLower, plural]);

  return { summary, generateResponse };
}

const PathfinderSentinel: React.FC<PathfinderSentinelProps> = ({
  dataPoints,
  routes,
  dwellLocations,
  actualDevices,
  selectedStudents: _selectedStudents,
  selectedDevices: _selectedDevices,
  timeRange: _timeRange,
  onStudentSelect,
  onDeviceSelect: _onDeviceSelect,
  onZoomToLocation,
  isDrawingAnalysisArea = false,
  analysisArea = null,
  onMapAction,
  itinerarySubmissions = [] as NonNullable<PathfinderSentinelProps['itinerarySubmissions']>,
}) => {
  const { singularLower } = useParticipantLabels();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  // Suggested prompts (wording follows participant labels from feature flags)
  const suggestedPrompts = useMemo(
    () => [
      {
        category: 'How to Use',
        icon: <HelpIcon fontSize="small" />,
        prompts: [
          'What can you help me with in Pathfinder?',
          'How do I analyze route data?',
          'How do I find dwell locations and stops?',
          'What data does Pathfinder analyze?',
        ],
      },
      {
        category: 'Co-Traveler Analysis',
        icon: <RouteIcon fontSize="small" />,
        prompts: [
          'Who were the co-travelers detected?',
          `Where did the ${singularLower} meet co-travelers?`,
          'Show co-traveler movement heatmap',
          `When did the ${singularLower} travel with others?`,
          `Compare ${singularLower} route with co-travelers`,
        ],
      },
      {
        category: 'Device Separation',
        icon: <CompareIcon fontSize="small" />,
        prompts: [
          'When did phone and vehicle separate?',
          'Are all devices traveling together?',
          'Show when devices split apart',
          `Where did the ${singularLower} leave their vehicle?`,
          'Did the watch stay with the phone?',
        ],
      },
      {
        category: 'Dwell Analysis',
        icon: <PlaceIcon fontSize="small" />,
        prompts: [
          'Where did they spend the most time?',
          'What are the common stop locations?',
          'How long were the dwell periods?',
          'Are there unexpected stops?',
        ],
      },
      {
        category: 'Route Analysis',
        icon: <SpeedIcon fontSize="small" />,
        prompts: [
          `What routes did this ${singularLower} take?`,
          'Are there any unusual route deviations?',
          'What is the typical travel pattern?',
          'Compare routes between devices',
        ],
      },
    ],
    [singularLower]
  );

  // Chat state
  // Chat history + busy flag live in the narrative store so they survive tab
  // switches and so the map HUD (fullscreen) shows and extends the same thread.
  const isLoading = useNarrativeStore((s) => s.sentinelLoading);
  const messages = useNarrativeStore((s) => s.sentinelMessages) as ChatMessage[];
  const setMessages = useNarrativeStore((s) => s.setSentinelMessages);
  const [question, setQuestion] = useState('');
  const [promptMenuAnchor, setPromptMenuAnchor] = useState<null | HTMLElement>(null);
  const promptMenuOpen = Boolean(promptMenuAnchor);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Analysis engine (summary stats + question answering) is shared with the map HUD.
  const { generateResponse } = usePathfinderSentinelEngine({
    dataPoints,
    routes,
    dwellLocations,
    actualDevices,
    itinerarySubmissions,
  });

  // Handle sending a question
  const handleSend = useCallback(async () => {
    if (!question.trim() || isLoading) return;
    const q = question.trim();
    setQuestion('');
    await askSentinel(q, generateResponse);

    // Scroll to bottom
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [question, isLoading, generateResponse]);


  // Handle action execution
  const handleAction = useCallback((action: PathfinderAction) => {
    switch (action.type) {
      case 'select_student':
        if (action.studentId && onStudentSelect) {
          onStudentSelect(action.studentId);
        }
        break;
      case 'zoom_to_location':
        if (action.location && onZoomToLocation) {
          onZoomToLocation(action.location);
        }
        break;
      case 'open_story_mode':
        usePathfinderStore.getState().setActiveToolId('story-mode');
        break;
      case 'open_story_panel':
        useNarrativeStore.getState().setPanelOpen('story', true);
        break;
      // Add more action handlers as needed
    }
  }, [onStudentSelect, onZoomToLocation]);

  // Clear chat
  // const handleClear = useCallback(() => {
  //   setMessages([]);
  // }, []);

  const colors = {
    accent: primaryColor,
    text: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    border: theme.palette.divider,
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      p: 1.5,
    }}>
      {/* Ask Sentinel Card - COP Style */}
      <Box sx={{ 
        mb: 2, 
        p: 1.5, 
        bgcolor: isDark ? 'rgba(30, 32, 48, 0.6)' : 'rgba(248, 250, 252, 0.9)',
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AskIcon sx={{ color: colors.accent, fontSize: '1rem' }} />
          <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.8rem' }}>
            Ask Sentinel
          </Typography>

          {/* Area Analysis Button */}
          {onMapAction && (
            <Tooltip title={isDrawingAnalysisArea ? "Drawing area... Click to cancel" : analysisArea ? "Area selected - ask a question" : "Draw area on map to analyze"}>
              <IconButton
                size="small"
                onClick={() => {
                  if (isDrawingAnalysisArea) {
                    onMapAction({ type: 'cancel_area_analysis' });
                  } else if (analysisArea) {
                    onMapAction({ type: 'cancel_area_analysis' });
                  } else {
                    onMapAction({ type: 'start_area_analysis' });
                  }
                }}
                sx={{
                  ml: 'auto',
                  color: isDrawingAnalysisArea ? '#FF9800' : analysisArea ? '#4CAF50' : colors.textSecondary,
                  bgcolor: isDrawingAnalysisArea ? 'rgba(255, 152, 0, 0.1)' : analysisArea ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                  '&:hover': {
                    bgcolor: isDrawingAnalysisArea ? 'rgba(255, 152, 0, 0.2)' : analysisArea ? 'rgba(76, 175, 80, 0.2)' : alpha(colors.accent, 0.1),
                  },
                  p: 0.5,
                  animation: isDrawingAnalysisArea ? 'pulse 1.5s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              >
                {analysisArea ? <CheckIcon sx={{ fontSize: '1rem' }} /> : <AreaSelectIcon sx={{ fontSize: '1rem' }} />}
              </IconButton>
            </Tooltip>
          )}
          
          {/* Export & Clear buttons */}
          {messages.length > 0 && (
            <>
              <Tooltip title="Export to PDF (with map)">
                <IconButton
                  size="small"
                  onClick={async () => {
                    await generateSentinelChatPDF(
                      {
                        title: 'Pathfinder Analysis Report',
                        subtitle: 'Route & Movement Intelligence',
                        messages: messages.map(m => ({
                          role: m.role,
                          content: m.content,
                          timestamp: m.timestamp,
                        })),
                        additionalContext: `Analysis Context: ${routes.length} routes, ${dataPoints.length} data points, ${dwellLocations.length} dwell locations`,
                        classification: 'Controlled Unclassified Information (CUI)',
                      },
                      'Pathfinder_Analysis_Report.pdf',
                      { mapElementId: 'pathfinder-map-container' }
                    );
                  }}
                  sx={{ 
                    ml: onMapAction ? 0 : 'auto',
                    color: colors.textSecondary, 
                    p: 0.5,
                    '&:hover': { bgcolor: alpha(colors.accent, 0.1) },
                  }}
                >
                  <PdfIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear chat">
                <IconButton
                  size="small"
                  onClick={() => setMessages([])}
                  sx={{ 
                    color: colors.textSecondary, 
                    p: 0.5,
                    '&:hover': { bgcolor: alpha(colors.accent, 0.1) },
                  }}
                >
                  <CloseIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {/* Area Analysis Status */}
        {(isDrawingAnalysisArea || analysisArea) && (
          <Box sx={{
            mb: 1,
            p: 1,
            bgcolor: isDrawingAnalysisArea ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)',
            borderRadius: 1,
            border: `1px solid ${isDrawingAnalysisArea ? '#FF9800' : '#4CAF50'}`,
          }}>
            {isDrawingAnalysisArea ? (
              <Typography sx={{ fontSize: '0.75rem', color: '#FF9800', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AreaSelectIcon sx={{ fontSize: '0.9rem' }} />
                Draw a polygon on the map to select area for analysis...
              </Typography>
            ) : analysisArea && (
              <>
                <Typography sx={{ fontSize: '0.75rem', color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <CheckIcon sx={{ fontSize: '0.9rem' }} />
                  Area selected! Found in this area:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${dataPoints.length} points`} 
                    size="small" 
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50' }} 
                  />
                  <Chip 
                    label={`${routes.length} routes`} 
                    size="small" 
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50' }} 
                  />
                  <Chip 
                    label={`${dwellLocations.length} dwells`} 
                    size="small" 
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50' }} 
                  />
                </Box>
                <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary, mt: 0.5 }}>
                  Ask a question like "What happened here?" or "Who visited this area?"
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Question Input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Ask anything or 'zoom to [entity]'..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              handleSend();
            }
          }}
          disabled={isLoading}
          InputProps={{
            sx: {
              bgcolor: isDark ? 'rgba(20, 22, 35, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              color: colors.text,
              fontSize: '0.8rem',
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: colors.accent },
              '&.Mui-focused fieldset': { borderColor: colors.accent },
            },
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Prompt Library Button */}
                <Tooltip title="Prompt Library">
                  <IconButton
                    size="small"
                    onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
                    sx={{ 
                      color: promptMenuOpen ? colors.accent : colors.textSecondary,
                      '&:hover': { color: colors.accent },
                    }}
                  >
                    <PromptIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
                {/* Send Button */}
                <IconButton
                  size="small"
                  onClick={() => handleSend()}
                  disabled={isLoading || !question.trim()}
                  sx={{ color: colors.accent }}
                >
                  {isLoading ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: '1rem' }} />}
                </IconButton>
              </Box>
            ),
          }}
        />

        {/* Prompt Library Menu */}
        <Menu
          anchorEl={promptMenuAnchor}
          open={promptMenuOpen}
          onClose={() => setPromptMenuAnchor(null)}
          PaperProps={{
            sx: {
              bgcolor: isDark ? 'rgba(30, 32, 48, 0.98)' : 'rgba(255, 255, 255, 0.98)',
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              maxHeight: 400,
              minWidth: 280,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }
          }}
        >
          <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${colors.border}` }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.text }}>
              📋 Analysis Prompts
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary }}>
              Click to auto-fill the chat input
            </Typography>
          </Box>
          {suggestedPrompts.map((category, catIdx) => (
            <Box key={catIdx}>
              <Box sx={{ px: 2, py: 0.75, bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)' }}>
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: 600, 
                  color: colors.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                  {category.icon}
                  {category.category}
                </Typography>
              </Box>
              {category.prompts.map((prompt, promptIdx) => (
                <MenuItem
                  key={promptIdx}
                  onClick={() => {
                    setQuestion(prompt);
                    setPromptMenuAnchor(null);
                    setTimeout(() => handleSend(), 100);
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    py: 0.75,
                    px: 2,
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <ListItemText primary={prompt} />
                </MenuItem>
              ))}
            </Box>
          ))}
        </Menu>
      </Box>

      {/* Chat Area */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Messages */}
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              mb: 1.5,
              p: 1,
              borderRadius: 1,
              bgcolor: msg.role === 'user' 
                ? alpha(primaryColor, 0.1)
                : alpha(theme.palette.grey[500], 0.1),
            }}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {msg.role === 'user' ? 'You' : '🛡️ Sentinel'}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 0.5,
                whiteSpace: 'pre-wrap',
                '& strong': { fontWeight: 600 },
              }}
            >
              {msg.content}
            </Typography>

            {/* Action Buttons */}
            {msg.actions && msg.actions.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                {msg.actions.map((action, actionIdx) => (
                  <Button
                    key={actionIdx}
                    size="small"
                    variant="outlined"
                    startIcon={action.icon}
                    onClick={() => handleAction(action)}
                    sx={{ 
                      fontSize: '0.7rem', 
                      py: 0.25,
                      textTransform: 'none',
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>
            )}
          </Box>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Analyzing data...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};

export default PathfinderSentinel;

